// Notification mode for conversation settings
export type NotificationMode = 'Default' | 'Muted' | 'NoticeOnly' | 'AutoOpen'

// Map numeric enum values from API to string NotificationMode
const NOTIFICATION_MODE_MAP: Record<number, NotificationMode> = {
  0: 'Muted',
  1: 'NoticeOnly',
  2: 'AutoOpen',
}

export function resolveNotificationMode(mode: NotificationMode | number | undefined | null): NotificationMode {
  if (mode == null || mode === 'Default') return 'NoticeOnly'
  if (typeof mode === 'number') return NOTIFICATION_MODE_MAP[mode] ?? 'NoticeOnly'
  return mode
}

const NOTIFICATION_MODE_TO_INT: Record<NotificationMode, number> = {
  Default: -1,
  Muted: 0,
  NoticeOnly: 1,
  AutoOpen: 2,
}

export function notificationModeToInt(mode: NotificationMode): number {
  return NOTIFICATION_MODE_TO_INT[mode] ?? 1
}

// Chat conversation types
export enum ConversationType {
  Direct = 0,
  Group = 1,
  Adventure = 2,
}

// Participant role in a conversation
export type ParticipantRole = 'Member' | 'Admin' | 'Owner'

// Participant in a conversation
export interface ConversationParticipant {
  id: string
  userId: string
  displayName: string
  role: ParticipantRole
  joinedAt: string
  lastReadAt: string | null
  isActive: boolean
  isOnline: boolean
  notificationMode?: NotificationMode | number
}

// Conversation settings request/response
export interface ConversationSettings {
  notificationMode: NotificationMode
}

// Conversation DTO
export interface ConversationDto {
  id: string
  name: string | null
  type: ConversationType
  createdAt: string
  lastMessageAt: string | null
  lastMessageContent: string | null
  isActive: boolean
  unreadMessageCount: number
  notificationMode?: NotificationMode | number
  messageCount: number
  participants: ConversationParticipant[]
  // For adventure chats
  adventureId?: string | null
  adventureName?: string | null
  adventureAvatar?: string | null
  // For group chats
  groupId?: string | null
  groupName?: string | null
  groupAvatar?: string | null
}

// Read receipt entry
export interface MessageReadBy {
  userId: string
  displayName: string
  readAt: string
}

// Reaction entry
export interface MessageReaction {
  userId: string
  displayName: string
  emoji: string
}

// Chat message DTO
export interface ChatMessageDto {
  id: string
  conversationId: string
  senderId: string
  senderName: string
  senderDisplayName?: string
  senderAvatarUrl: string | null
  content: string
  imageUrl?: string | null
  timestamp: string
  isRead: boolean
  isDeleted: boolean
  isEdited: boolean
  isOwnMessage?: boolean
  isSystemMessage?: boolean
  metadata?: string | null
  editedAt: string | null
  readBy?: MessageReadBy[]
  reactions?: MessageReaction[]
}

// Paginated messages response
export interface MessagesResponse {
  items: ChatMessageDto[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasMore: boolean
}

// Request to send a message
export interface SendMessageRequest {
  content: string
  imageId?: string
}

// Response from image upload
export interface ChatImageUploadResponse {
  imageId: string
  imageUrl: string
  width: number
  height: number
  fileSize: number
}

// Request to create a direct conversation
export interface CreateDirectConversationRequest {
  recipientId: string
}

// Request to create a group conversation
export interface CreateGroupConversationRequest {
  name: string
  participantIds: string[]
}

// User search result for chat
export interface ChatUserSearchResult {
  id: string
  userName: string
  displayName: string
  avatarUrl: string | null
  isOnline: boolean
}

// Chat notification
export interface ChatNotification {
  id: string
  conversationId: string
  conversationName: string | null
  conversationType: ConversationType
  senderId: string
  senderName: string
  senderAvatarUrl: string | null
  messagePreview: string
  timestamp: string
  isRead: boolean
}

// Online friend
export interface OnlineFriend {
  id: string
  userName: string
  displayName: string
  avatarUrl: string | null
  lastSeenAt: string
}

// Typing indicator
export interface TypingIndicator {
  conversationId: string
  userId: string
  userName: string
  isTyping: boolean
}
